import { useCallback, useMemo } from 'react';

import { Box, Icon, Paragraph, Tag, Text, TextButton } from '@zillow/constellation-10';
import { IconHandshakeFilled } from '@zillow/constellation-icons';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { useRentalManagerUserPropertySummaryWebApiHandler_leases } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import selectPropertyUtils from '../../../../../srcLegacy/shared/utils/selectPropertyUtils';

import SelectPropertyConstants from '../../../../../srcLegacy/shared/constants/SelectPropertyConstants';
import TimeConstants from '../../../../../srcLegacy/shared/constants/TimeConstants';
import PropertiesPageCSEvents from '../../../../../srcLegacy/shared/model/ClickstreamEvent/PropertiesPageCSEvents';

import WhoHasSignedDescription from '../../../../../srcLegacy/shared/components/ConnectedLeasesModule/components/WhoHasSignedDescription';
import ConnectedModuleCard from '../../../../../srcLegacy/shared/components/ConnectedModuleCard';
import ConnectedStatus from '../../../../../srcLegacy/shared/components/ConnectedStatus';
import EmptyState from '../../../../../srcLegacy/shared/components/EmptyState';
import ErrorMessagesAlert from '../../../../../srcLegacy/shared/components/ErrorMessagesAlert';
import SimpleCard from '../../../../../srcLegacy/shared/components/SimpleCard';
import { useAuth } from '../../../../components/AuthProvider';

import { NoUnderlineAnchor } from '../../../../../srcLegacy/shared/styles';

import GeneratedLeaseUpsell from './GeneratedLeaseUpsell';

import type {
  UserPropertySummaryLeasesLeaseCardsWebDto,
  UserPropertySummaryLeasesTextTypeWebDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

const MAX_LEASE_CARDS_DISPLAY = 2;

type ConnectedStatusType = 'info' | 'neutral' | 'success' | 'warn' | 'in_progress' | 'caution';

type Props = {
  listingAlias: string;
  isLeaseBuilderRestricted?: boolean;
  propertyState?: string;
};

const LeasesModule = ({ listingAlias, isLeaseBuilderRestricted = false, propertyState }: Props) => {
  const { user } = useAuth();

  const leasesPath = selectPropertyUtils.getPathByType(
    SelectPropertyConstants.LEASES as keyof typeof SelectPropertyConstants,
    listingAlias,
  );

  const { data, error, isLoading } = useRentalManagerUserPropertySummaryWebApiHandler_leases(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10, retry: false },
  );

  const leaseCards = (data?.leaseCards ?? []).slice(0, MAX_LEASE_CARDS_DISPLAY);
  const hasLeases = leaseCards.length > 0;
  const isEligibleForGeneratedLease = data?.isEligibleForGeneratedLease ?? false;

  const handleDetailsClick = useCallback(() => {
    if (user) {
      PropertiesPageCSEvents.LeasesCardDetailsClick({ user });
    }
  }, [user]);

  const renderCTA = useMemo(() => {
    if (isEligibleForGeneratedLease && !hasLeases) return null;

    return (
      <TextButton tone="brand" asChild onClick={handleDetailsClick}>
        <a href={leasesPath || ''} target="_self">
          Details
        </a>
      </TextButton>
    );
  }, [leasesPath, hasLeases, isEligibleForGeneratedLease, handleDetailsClick]);

  if (error) {
    const partialErrorMessages = DatarouterError.getErrorDetails(error)?.partialMessages || [
      { text: 'Unable to load leases. Please refresh the page to try again.' },
    ];

    return (
      <ConnectedModuleCard data-testid="LeasesModule" title="Leases">
        <ErrorMessagesAlert appearance="warning" errorMessages={partialErrorMessages} />
      </ConnectedModuleCard>
    );
  }

  const renderDescription = (
    description?: UserPropertySummaryLeasesTextTypeWebDto | null,
    signeeList: Array<UserPropertySummaryLeasesTextTypeWebDto> = [],
  ) => {
    if (!description?.text) return null;

    if (description.type === 'active') {
      return (
        <Box css={{ marginTop: '400' }}>
          <WhoHasSignedDescription signeeList={signeeList} />
        </Box>
      );
    }

    return (
      <Paragraph
        textStyle="body-sm"
        css={{
          marginTop: '200',
          ...(description.type === 'caution' ? { color: 'text.feedback.critical.strong' } : {}),
        }}
      >
        {description.text}
      </Paragraph>
    );
  };

  const renderLeaseCard = (card: UserPropertySummaryLeasesLeaseCardsWebDto, idx: number) => {
    const { latestExtension } = card;
    const href = latestExtension?.ctaButton?.url ?? card.ctaButton?.url;

    return (
      <NoUnderlineAnchor key={card.leaseName ?? idx} href={href} target="_self">
        <SimpleCard isDisabled withChevron>
          <Text textStyle="body-sm-bold" css={{ marginBottom: '200', display: 'block' }}>
            {card.leaseName || `Lease #${idx + 1}`}
          </Text>

          {latestExtension ? (
            <>
              <Box css={{ display: 'flex', gap: '200', flexWrap: 'wrap' }}>
                <ConnectedStatus
                  message={latestExtension.status.text}
                  type={latestExtension.status.type as ConnectedStatusType}
                  size="sm"
                />
                <Tag tone="info" size="sm">
                  Lease renewal
                </Tag>
              </Box>
              {latestExtension.dateRange && (
                <Paragraph textStyle="body-sm" css={{ marginTop: '200' }}>
                  {latestExtension.dateRange}
                </Paragraph>
              )}
              {renderDescription(latestExtension.description, card.signeeList)}
            </>
          ) : (
            <>
              <Box css={{ display: 'flex', gap: '200', flexWrap: 'wrap' }}>
                <ConnectedStatus
                  message={card.leaseStatus.text}
                  type={card.leaseStatus.type as ConnectedStatusType}
                  size="sm"
                />
              </Box>
              {card.dateRange && (
                <Paragraph textStyle="body-sm" css={{ marginTop: '200' }}>
                  {card.dateRange}
                </Paragraph>
              )}
              {renderDescription(card.leaseDescription, card.signeeList)}
            </>
          )}
        </SimpleCard>
      </NoUnderlineAnchor>
    );
  };

  const renderLeaseCards = () => (
    <Box
      css={{
        display: 'grid',
        gridTemplateColumns: { base: 'repeat(1, 1fr)', lg: leaseCards.length > 1 ? 'repeat(2, 1fr)' : '1fr' },
        gap: { base: '200', lg: '400' },
      }}
    >
      {leaseCards.map((card, idx) => renderLeaseCard(card, idx))}
    </Box>
  );

  const renderEmpty = () => {
    if (isEligibleForGeneratedLease) {
      return <GeneratedLeaseUpsell listingAlias={listingAlias} state={propertyState} />;
    }

    return (
      <EmptyState
        data-testid="EmptyLeasesCardSimple"
        icon={<Icon size="md" css={{ color: 'icon.subtle' }} render={<IconHandshakeFilled />} />}
        heading="Active leases will appear here"
        headingLevel={4}
        body={isLeaseBuilderRestricted ? 'Upload a lease' : 'Upload or create a lease'}
        align="left"
      />
    );
  };

  return (
    <ConnectedModuleCard
      data-testid="LeasesModule"
      title="Leases"
      ctaComponent={renderCTA}
      ctaFontType="body-sm"
      justifyHeadingContent={'center'}
      isLoading={isLoading}
    >
      {!isLoading && (hasLeases ? renderLeaseCards() : renderEmpty())}
    </ConnectedModuleCard>
  );
};

export default LeasesModule;
